import { useId } from 'react';
import { DatePicker } from '@roger/ui-components';

import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';
import { worksheetErrorInputClass } from './validationErrors';
import { WorksheetFieldError } from './WorksheetFieldError';

interface WorksheetDateInputProps {
  label: string;
  value?: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /** Save-time validation message for this field. */
  error?: string;
  /** `data-field-anchor` value, so the summary panel can scroll here. */
  fieldAnchor?: string;
}

/**
 * Labeled worksheet date field: calendar popup + manual typing, commits on
 * blur/Enter, saves bare YYYY-MM-DD strings and emits null (never '') on clear.
 */
export function WorksheetDateInput({
  label,
  value,
  onChange,
  disabled,
  error,
  fieldAnchor,
}: WorksheetDateInputProps) {
  // Bind the label to the typable input by id, and don't wrap the picker in
  // the label: a wrapping <label> names the popover trigger button (the first
  // labelable element inside it) instead of the text input.
  const inputId = useId();
  const errorId = `${inputId}-error`;

  return (
    // No space-y here: sibling fields wrap a bare text node in their <label>,
    // so their control gets no top margin. An explicit <label> element would
    // make space-y apply and push date fields 4px below their row neighbours.
    <div
      className='text-xs text-muted-foreground'
      data-field-anchor={fieldAnchor}
    >
      <label htmlFor={inputId} className='block'>
        {label}
      </label>
      <DatePicker
        id={inputId}
        datePickerSize='sm'
        typable
        className={error ? `w-full ${worksheetErrorInputClass}` : 'w-full'}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        date={parseISOToLocalDate(value ?? '')}
        onDateChange={date => onChange(date ? formatDateToISO(date) : null)}
      />
      <WorksheetFieldError id={errorId} error={error} />
    </div>
  );
}
